import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useLeaderCriteriaService } from "~pages/LeaderCriteria/hooks/useLeaderCriteriaService.ts";
import { LeaderCriteriaRulesAction } from "~pages/LeaderCriteria/LeaderCriteriaListPage/components/LeaderCriteriaRulesAction.tsx";
import { useFetchLeaderCriteriaList } from "~pages/LeaderCriteria/LeaderCriteriaListPage/hooks/useFetchLeaderCriteriaList.ts";
import { useGetLeaderCriteriaTableColumns } from "~pages/LeaderCriteria/LeaderCriteriaListPage/hooks/useGetLeaderCriteriaTableColumns.tsx";
import type { LeaderCriteria } from "~pages/LeaderCriteria/types/leaderCriteria.ts";

const LeaderCriteriaListPage = () => {
  const paginationConfig = useHandlePagination(10, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [massEditMode, setMassEditMode] = useState(false);

  const { hasPermission } = useUserPermissions();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.leaderCriteria.listPage;
  const columns = useGetLeaderCriteriaTableColumns(massEditMode);

  const { resetPagination } = paginationConfig;

  const handleSearch = useCallback(
    (value: string) => {
      resetPagination();
      setSearchText(value);
    },
    [resetPagination],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      nameContains: searchText,
    }),
    [paginationConfig.pageSize, paginationConfig.offset, searchText],
  );

  const { data, refetch, isPending } = useFetchLeaderCriteriaList({ customParams: listParams });
  const { deleteLeaderCriteria, isDeletePending } = useLeaderCriteriaService();

  const canReadRules = hasPermission(PermissionNames.LEADER_CRITERIA_SET, PermissionOperations.READ);

  const getNavigateUrl = (leaderCriteriaId = "") =>
    `/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/manage/${leaderCriteriaId}`;

  return (
    <DataTable<LeaderCriteria>
      handleRefresh={async () => {
        await refetch();
      }}
      title={formatMessage({ id: "leaders.qualification" })}
      columns={columns}
      rowActions={{
        custom: {
          rules: {
            hidden: () => !canReadRules,
            renderAction: (row) => <LeaderCriteriaRulesAction leaderCriteriaId={row.leaderCriteriaId} />,
          },
        },
        base: {
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.leaderCriteriaId)),
          },
          delete: {
            onClick: (row) => deleteLeaderCriteria(row.leaderCriteriaId),
            loading: isDeletePending,
          },
        },
      }}
      rowKey={"leaderCriteriaId"}
      rowNameKey={"name"}
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.leaderCriteria`}
      isDataLoading={isPending}
      handleAdd={() => navigate(getNavigateUrl())}
      addButtonIntlKey={"leaders.qualification"}
      searchConfig={{
        handleSearch,
        searchText,
      }}
      permissionNames={[PermissionNames.LEADER_QUALIFIER]}
      onMassEditModeChangedHandler={setMassEditMode}
      massEditMode={massEditMode}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default LeaderCriteriaListPage;
